(() => {
  const normalizeMoneySpacing = (scope = document) => {
    let root = scope;
    if (root.nodeType === Node.TEXT_NODE) root = root.parentElement;
    if (!root || !root.querySelectorAll) return;

    const moneyElements = [];
    if (root.matches?.('.money')) moneyElements.push(root);
    root.querySelectorAll('.money').forEach((element) => moneyElements.push(element));

    moneyElements.forEach((element) => {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();

      while (textNode) {
        const normalized = textNode.nodeValue.replace(/\bNIS(?=\d)/g, 'NIS ');
        if (normalized !== textNode.nodeValue) textNode.nodeValue = normalized;
        textNode = walker.nextNode();
      }
    });
  };

  const initQuote = (root) => {
    if (!root || root.dataset.leshemsQuoteReady === 'true') return;

    const panel = root.querySelector('[data-leshems-quote-panel]');
    const toggles = root.querySelectorAll('[data-leshems-quote-toggle]');
    if (!panel || !toggles.length) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const setOpen = (open, focusTarget) => {
      panel.hidden = !open;
      toggles.forEach((button) => button.setAttribute('aria-expanded', String(open)));

      if (open) {
        let target = focusTarget;
        if (!(target instanceof Element)) {
          target = focusTarget === 'customization'
            ? panel.querySelector('[data-leshems-quote-customization]')
            : panel.querySelector('input:not([type="hidden"])');
        }
        window.requestAnimationFrame(() => target?.focus({ preventScroll: true }));
        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
      }
    };

    toggles.forEach((button) => {
      button.addEventListener('click', () => {
        const shouldOpen = panel.hidden || button.dataset.leshemsQuoteFocus === 'customization';
        setOpen(shouldOpen, button.dataset.leshemsQuoteFocus);
      });
    });

    // After a contact-form post-back (success or validation errors) the panel
    // must be visible and the message focused, otherwise the result is hidden.
    const message = panel.querySelector('[data-leshems-quote-message]');
    if (message) setOpen(true, message);

    root.dataset.leshemsQuoteReady = 'true';
  };

  const soldOutQuoteContainers = new WeakSet();

  const updateSoldOutQuote = (wrapper, variant) => {
    wrapper.hidden = !variant || variant.available !== false;
    if (!variant) return;

    const variantInput = wrapper.querySelector('[data-leshems-quote-variant-id]');
    const skuInput = wrapper.querySelector('[data-leshems-quote-sku]');
    const urlInput = wrapper.querySelector('[data-leshems-quote-product-url]');
    if (variantInput) variantInput.value = variant.id;
    if (skuInput) skuInput.value = variant.sku || '';
    if (urlInput) {
      const url = new URL(urlInput.value, window.location.origin);
      url.searchParams.set('variant', variant.id);
      urlInput.value = url.toString();
    }
  };

  const initSoldOutQuote = (wrapper) => {
    // A contact form response must remain visible after Shopify's post-back.
    if (wrapper.querySelector('[data-leshems-quote-message]')) wrapper.hidden = false;

    const container = wrapper.closest('[data-section-type="product"]');
    if (!container || soldOutQuoteContainers.has(container)) return;

    // The legacy PDP emits this event on its own root without bubbling.
    // Listen on that root so another product's variant cannot change this form.
    container.addEventListener('variantChange', (event) => {
      container.querySelectorAll('[data-leshems-sold-out-quote]').forEach((quote) => {
        updateSoldOutQuote(quote, event.detail?.variant);
      });
    });
    soldOutQuoteContainers.add(container);
  };

  const forEachWithin = (scope, selector, callback) => {
    if (scope.matches?.(selector)) callback(scope);
    scope.querySelectorAll(selector).forEach(callback);
  };

  const initAll = (scope = document) => {
    normalizeMoneySpacing(scope);
    forEachWithin(scope, '[data-leshems-quote]', initQuote);
    forEachWithin(scope, '[data-leshems-sold-out-quote]', initSoldOutQuote);
    scope.querySelectorAll('[data-leshems-legacy-filters]').forEach((root) => {
      if (root.dataset.leshemsFilterBridgeReady === 'true') return;

      root.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link || link.classList.contains('leshems-legacy-filters__clear')) return;
        if (!window.location.search) return;

        const target = new URL(link.href, window.location.origin);
        const current = new URLSearchParams(window.location.search);
        current.forEach((value, key) => {
          if (key === 'page') return;
          if (!target.searchParams.getAll(key).includes(value)) {
            target.searchParams.append(key, value);
          }
        });

        event.preventDefault();
        window.location.assign(target.toString());
      });

      root.dataset.leshemsFilterBridgeReady = 'true';
    });
  };

  const observeMoney = () => {
    if (document.documentElement.dataset.leshemsMoneyObserver === 'true') return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          normalizeMoneySpacing(mutation.target);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Flex PDP hot reload and quick shop insert fresh quote forms
            // without emitting Shopify's section-load event.
            initAll(node);
          } else if (node.nodeType === Node.TEXT_NODE) {
            normalizeMoneySpacing(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true
    });
    document.documentElement.dataset.leshemsMoneyObserver = 'true';
  };

  const restoreNavFocusOnEscape = () => {
    // The browser can close an open nav <details> on Escape before this
    // listener runs, so track the relevant summary as focus moves through it
    // rather than querying for [open] inside the keydown handler itself.
    let lastNavSummary = null;

    document.addEventListener('focusin', (event) => {
      const details = event.target.closest('.site-nav__details');
      if (!details) return;
      const summary = details.querySelector(':scope > summary');
      if (summary) lastNavSummary = summary;
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !lastNavSummary) return;
      const summary = lastNavSummary;

      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active === document.body || active === document.documentElement) {
          summary.focus();
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    observeMoney();
    restoreNavFocusOnEscape();
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('collection:reloaded', () => initAll());
})();
